import { useCallback, useEffect, useRef } from 'react';

import {
    EventType,
    MenuItem,
    NavbarType,
    useAppBridge,
    useCurrentPage,
    useEventBusOn,
    useMenuAuthority,
    useMenuRouter,
    useMenuStore,
    useQuery,
    useRouterRegistry,
    useUserContext,
    useWebviewBridge,
    useWebviewContext,
    useWebviewReactivated,
} from 'scbk-services';
import { useNavBar } from 'scbk-shared-module';

const DATEPICKER_EVENT = 'datepicker:visibility-change';
const DIALOG_EVENT = 'dialog:visibility-change';
/**
 * 모바일뱅킹 하단 네비게이션바 제어 컴포넌트
 * @returns
 */
const NavigationBarController: React.FC = () => {
    const { query } = useQuery();
    const webviewType = query['webviewType'];
    const userCtx = useUserContext(); // 유저정보 컨텍스트
    const webviewContext = useWebviewContext();
    const { currentPageId } = useCurrentPage();
    //const currentRouteObjectConfig = useRouterRegistry((state) => state.currentRouteObjectConfig);
    const currentPageIdRef = useRef<string>(currentPageId);
    const { doNShowMainNavBar, doNHideMainNavBar, doNGoMain } = useWebviewBridge();
    const { doNSetWebviewStorage } = useAppBridge();
    const { navBarControl } = useNavBar();
    const { findByPath } = useMenuStore();
    const { checkMenuInfo } = useMenuAuthority();
    const { goMenu } = useMenuRouter();

    /**
     * <pre>
     * 하단탭 제어
     * 웹뷰가 resume 될때 페이지ID를 기준으로 navbar 노출여부를 확인하여 노출/비노출 처리
     * </pre>
     */
    const syncNavBar = useCallback(async () => {
        const currentRouteObjectConfig = useRouterRegistry.getState().currentRouteObjectConfig;
        if (!currentRouteObjectConfig) {
            console.warn('currentRouteObjectConfig is undefined');
            return;
        }

        console.log(
            '### NavigationBarController syncNavBar currentPageId: ',
            currentPageId,
            ', currentRouteObjectConfig: ',
            currentRouteObjectConfig,
        );

        const options = currentRouteObjectConfig.options ?? {};
        const showNavbar = !options ? false : (options.showNavbar ?? false);
        navBarControl(showNavbar);
    }, [navBarControl]);

    /**
     * 메인 네비바 이동 전용
     * @param menu 메뉴정보
     * @param id 네비바 ID
     */
    const goMain = useCallback(async (menu: MenuItem, id?: string) => {
        //********************************************************************
        //메뉴 체크
        //TOOD 세션클리어
        //********************************************************************
        const checkedMenu = await checkMenuInfo(menu); // 세션클리어
        if (!checkedMenu) {
            return;
        }

        //********************************************************************
        //이전에 저장된 restore data 정보 제거
        //********************************************************************
        //clearRestoreData();
        //clearMenuContext();

        //편한뱅킹 이체 탭인 경우 업무화면이기때문에 navbarType이 설정안되어 있음..
        //navbarType이 없는경우 네이티브에서 전달한 id 값을 설정하도록 한다.
        const navbarType = (menu.fields?.['navbarType'] ?? id) as NavbarType;
        await doNGoMain(navbarType);
    }, []);

    //**********************************
    //웹뷰 활성화여부 체크
    //**********************************
    useWebviewReactivated({
        onReactivated: () => {
            console.log(
                `# NavigationBarController onReactivated... webviewType=${webviewType} isLogin=${userCtx.isLoginUser()}`,
            );
        },
        onResume: async () => {
            console.log(
                `# NavigationBarController onResume... webviewType=${webviewType} isLogin=${userCtx.isLoginUser()}`,
            );

            webviewContext.resume();
            syncNavBar();
        },
    });

    //**********************************************
    //하단탭 Show/Hide 이벤트 처리
    //**********************************************
    useEventBusOn({
        eventType: EventType.ShowNavbar,
        listener: async () => {
            console.log('# NavigationBarController EventType.ShowNavbar');
            doNShowMainNavBar();
        },
    });

    useEventBusOn({
        eventType: EventType.HideNavbar,
        listener: async () => {
            console.log('# NavigationBarController EventType.HideNavbar');
            doNHideMainNavBar();
        },
    });

    useEffect(() => {
        currentPageIdRef.current = currentPageId;
    }, [currentPageId]);

    //**********************************************
    // 네이티브 하단 탭 클릭 메인 이동 처리
    //**********************************************
    useEffect(() => {
        const handleGoMain = async (event: Event) => {
            const e = event as CustomEvent;

            const detail = e.detail;
            const { id, url } = detail;

            console.log(`# NavigationBarController webview:goMain event=${e} id=${id}, url=${url}`);

            if (!id || !url) {
                return;
            }

            const menu = findByPath(url);

            if (!menu) {
                console.log(
                    `# NavigationBarController  webview:goMain 등록된 메뉴정보가 없습니다. url=${url}`,
                );
                return;
            }

            //********************************
            //로딩처리
            //********************************
            //loadingManager.open();

            const ctx = {
                source: 'navbar_click',
                id,
                timestamp: Date.now(),
            };
            await doNSetWebviewStorage(`navigationContext`, JSON.stringify(ctx));
            localStorage.setItem('navigationContext', JSON.stringify(ctx));

            goMain(menu, id);
        };

        window.addEventListener('webview:goMain', handleGoMain);
        return () => window.removeEventListener('webview:goMain', handleGoMain);
    }, [findByPath, goMain]);

    //**********************************************
    // datepicker open/close event 처리
    // 하단 네비바 노출여부를 처리한다.
    //**********************************************
    useEffect(() => {
        const handleVisibilityEvent = (event: Event) => {
            console.log('# NavigationBarController ui-web visibility-change event', event);
            const e = event as CustomEvent;
            const detail = e.detail;
            if (!detail) {
                return;
            }

            const { open = false } = detail;
            navBarControl(false, open);
        };

        window.addEventListener(DATEPICKER_EVENT, handleVisibilityEvent);
        window.addEventListener(DIALOG_EVENT, handleVisibilityEvent);
        return () => {
            window.removeEventListener(DATEPICKER_EVENT, handleVisibilityEvent);
            window.removeEventListener(DIALOG_EVENT, handleVisibilityEvent);
        };
    }, [navBarControl]);

    /**
     * 앱 최적화 메뉴이동 event 처리
     */
    useEffect(() => {
        const handleGoMenu = async (event: Event) => {
            console.log('# handleGoMenu', event);
            const e = event as CustomEvent;
            const detail = e.detail;
            if (!detail) {
                return;
            }

            const { id, options = '{}' } = detail;
            if (!id) {
                return;
            }

            let opt = {};
            try {
                opt = JSON.parse(options);
            } catch (e) {
                console.log(e);
            }

            //TODO goMenu처리
            goMenu(id, opt);
        };

        window.addEventListener('webview:goMenu', handleGoMenu);
        return () => window.removeEventListener('webview:goMenu', handleGoMenu);
    }, []);

    return null;
};

export { NavigationBarController };
